'use client';
import React from 'react';
import { DateTime } from 'luxon';

import Layout from '../lib/components/Layout';
import { SessionContext } from '@benzinga/session-context';
import ColumnsDefinition from './ColumnsDef';
import { formatRequestDate } from '../lib/utils';
import { fetchOptions } from './Services';
import { CALENDAR_FETCH_PAGE_SIZE, useCalendarPagedFetch } from '../lib/useCalendarPagedFetch';

import { ICalendarProps, IDateRange } from '../lib/interfaces';
import { ISetParams } from '@benzinga/calendar-manager';
import { OptionsActivity } from '@benzinga/calendar-option-activity-manager';
import { CommonCalendarQueryParams } from '@benzinga/calendar-commons';

export const fetchOptionsData = fetchOptions;
export const OptionsCalendarColumnsDef = ColumnsDefinition;

export const OptionsCalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  calendarType,
  hasStockTableStyling,
  height,
  initialData,
  initialDateRange,
  initialTicker,
  leftSlot,
  onReady,
}) => {
  const session = React.useContext(SessionContext);

  const dateRange: IDateRange = initialDateRange ?? {
    date_from: formatRequestDate(DateTime.now().minus({ months: 1 }).toJSDate()),
    date_to: formatRequestDate(new Date()),
  };

  const initialParams = React.useMemo(
    () => ({
      dateFrom: dateRange.date_from,
      dateTo: dateRange.date_to,
      ...(initialTicker && { symbols: initialTicker }),
    }),
    [dateRange.date_from, dateRange.date_to, initialTicker],
  );

  const buildQuery = React.useCallback(
    (filterParams: Record<string, unknown>, page: number): CommonCalendarQueryParams => {
      const dateFrom = typeof filterParams.dateFrom === 'string' ? filterParams.dateFrom : undefined;
      const dateTo = typeof filterParams.dateTo === 'string' ? filterParams.dateTo : undefined;
      const symbolsValue = filterParams.symbols;
      const symbols =
        typeof symbolsValue === 'string'
          ? symbolsValue
              .split(',')
              .map(symbol => symbol.trim())
              .filter(Boolean)
          : Array.isArray(symbolsValue)
            ? symbolsValue
            : undefined;

      return {
        dateFrom,
        dateTo,
        page,
        pageSize: CALENDAR_FETCH_PAGE_SIZE,
        symbols,
      };
    },
    [],
  );

  const { loading, onFilterChanged, onViewportChanged, setTableData, tableData } = useCalendarPagedFetch<
    OptionsActivity,
    ISetParams
  >({
    fetchPage: (params, page) =>
      fetchOptionsData(session.getSession(), buildQuery(params as Record<string, unknown>, page)).then(
        items => items as OptionsActivity[],
      ),
    initialData,
    initialParams,
  });

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData ?? [], initialParams);
    }
  }, [calendarData?.serverSide, initialData, initialParams, setTableData]);

  function onTableReady() {
    if (initialData) {
      setTableData(initialData, initialParams);

      if (onReady) {
        setTimeout(() => onReady());
      }
    } else {
      onFilterChanged(initialParams);

      if (onReady) {
        onReady();
      }
    }
  }

  return (
    <Layout
      calendar={calendar}
      calendarData={calendarData}
      calendarType={calendarType}
      columnDefs={ColumnsDefinition}
      enableSearch
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      initialDateRange={dateRange}
      isGated={true}
      leftSlot={leftSlot}
      onFilterChanged={onFilterChanged}
      onTableReady={onTableReady}
      tableData={tableData}
      tableLoading={loading}
      theme="modern"
      ticker={initialTicker}
      viewportChange={onViewportChanged}
    />
  );
};
